import type { ArdoqId } from '@ardoq/api-types';
import { AppModules } from 'appContainer/types';
import { Features } from '@ardoq/features';
import { disabledByFeatureFlagBoundary } from 'router/declarative/featureFlagBoundary';
import { i18nTitle } from 'router/declarative/i18nTitle';
import { param } from 'router/declarative/paramRegistry';
import {
  IndexRouteContent,
  PathRouteContent,
  RouteNode,
} from 'router/declarative/routeNode';
import { presentationParamDeclarations } from 'router/presentation/presentationParamDeclarations';
import { dashboardFilterValuesParam } from './dashboardFilterValuesParam';

const readerState = param<ArdoqId>({
  fromString: value => value,
  toString: value => value,
});

const dashboardFolderId = param<ArdoqId>({
  fromString: value => value,
  toString: value => value,
});

export const routes = {
  path: '/dashboard',
  appModule: AppModules.DASHBOARDS,
  children: [
    {
      path: { index: true },
      title: i18nTitle('dashboard:navigation.overviewTitle', 'Dashboards'),
      content: async (): IndexRouteContent =>
        (await import('../DashboardOverview/DashboardOverview'))
          .DashboardOverview,
    },
    {
      path: '/builder',
      searchParams: { dashboardFolderId },
      title: i18nTitle(
        'dashboard:navigation.builderTitle',
        'Dashboard Builder'
      ),
      content: async (): PathRouteContent =>
        (await import('../DashboardBuilder/DashboardBuilder')).DashboardBuilder,
      children: [
        {
          path: '/:dashboardId',
        },
      ],
    },
    {
      path: '/reader',
      title: i18nTitle('dashboard:navigation.readerTitle', 'Dashboard Reader'),
      searchParams: { state: readerState },
      children: [
        {
          path: '/:dashboardId',
          searchParams: {
            ...presentationParamDeclarations,
            dashboardFilterValues: dashboardFilterValuesParam,
          },
          content: async (): PathRouteContent =>
            (await import('../DashboardReader/DashboardReader'))
              .DashboardReader,
        },
      ],
    },
    {
      path: '/color-theme-settings',
      title: i18nTitle(
        'dashboard:navigation.colorThemeSettingsTitle',
        'Dashboard Color Theme Settings'
      ),
      content: async (): PathRouteContent =>
        (
          await import('../DashboardColorThemeSettings/DashboardColorThemeSettings')
        ).DashboardColorThemeSettings,
    },
    {
      path: '/usage-metrics',
      title: i18nTitle(
        'dashboard:navigation.usageMetricsTitle',
        'Usage metrics'
      ),
      content: disabledByFeatureFlagBoundary(
        [Features.DASHBOARDS_RESTRICTED_DISABLE_USAGE_METRICS],
        { pageName: 'Usage metrics' }
      ),
      children: [
        {
          path: { index: true },
          content: async (): IndexRouteContent =>
            (await import('../UsageMetrics/UsageMetricsDashboard'))
              .UsageMetricsDashboard,
        },
      ],
    },
  ],
} as const satisfies RouteNode;
